/**
 * Config that drives usePropertyForm and PropertyForm for every asset type.
 * Adding a new type = add one entry here, zero code changes elsewhere.
 *
 * Flags:
 *   hasArea        – show total area (area + unit_area) → payload: total_size, total_unit
 *   hasUseArea     – show usable area (use_area + use_unit_area) → payload: living_size, living_unit
 *   hasRooms       – show floor / bedroom / bathroom
 *   hasLandArea    – show rai / ngan / wah (land only)
 *   hasDeeds       – show front_deeds / back_deeds upload
 *   hasFirstHand   – require first_hand field
 *   hasFurniture   – require furniture_included field
 *   hasSellOut     – show เซ้ง option + sellout_price
 *
 *   floorPayloadKey    – backend field name for floor (null = not sent)
 *   bedroomPayloadKey  – backend field name for bedroom (null = not sent)
 *   bathroomPayloadKey – backend field name for bathroom (null = not sent)
 *
 *   defaultUseAreaUnit – preset value for use_unit_area (null = user must choose)
 */

const base = {
  hasArea: true,
  hasUseArea: true,
  hasRooms: true,
  hasLandArea: false,
  hasDeeds: true,
  hasFirstHand: true,
  hasFurniture: true,
  hasSellOut: true,
  defaultUseAreaUnit: null,
};

export const PROPERTY_CONFIGS = {
  house: {
    ...base,
    title: 'บ้านเดี่ยว',
    endpoint: 'house',
    dbName: 'KeppDataCreateHouse',
    storeName: 'CreateHouse',
    storageKey: 'HouseData',
    floorPayloadKey: 'floor_house',
    bedroomPayloadKey: 'bedrooms_house',
    bathroomPayloadKey: 'bathrooms_house',
  },

  condo: {
    ...base,
    title: 'คอนโด',
    endpoint: 'condo',
    dbName: 'KeppDataCreateCondo',
    storeName: 'CreateCondo',
    storageKey: 'CondoData',
    floorPayloadKey: 'floor_condo',
    bedroomPayloadKey: 'bedrooms_condo',
    bathroomPayloadKey: 'bathrooms_condo',
    hasArea: false,    // condo has no total area – only use_area
    hasDeeds: false,   // condo deed upload is disabled
    hasSellOut: false, // condo does not support เซ้ง
    defaultUseAreaUnit: 'ตารางเมตร',
  },

  townhouse: {
    ...base,
    title: 'ทาวน์เฮ้าส์',
    endpoint: 'townhouse',
    dbName: 'KeppDataCreateTownhouse',
    storeName: 'CreateTownhouse',
    storageKey: 'TownhouseData',
    // Note: TownhouseForm state used 'floor_house' but payload sends 'floor_townhouse'
    floorPayloadKey: 'floor_townhouse',
    bedroomPayloadKey: 'bedrooms_townhouse',
    bathroomPayloadKey: 'bathrooms_townhouse',
  },

  commercial: {
    ...base,
    title: 'อาคารพาณิชย์',
    endpoint: 'commercial_building',
    dbName: 'KeppDataCreateCommercial',
    storeName: 'CreateCommercial',
    storageKey: 'CommercialData',
    // Note: CommercialForm state used 'floor_commercial' but payload sends 'floor_commercial_building'
    floorPayloadKey: 'floor_commercial_building',
    bedroomPayloadKey: 'bedrooms_commercial_building',
    bathroomPayloadKey: 'bathrooms_commercial_building',
  },

  land: {
    ...base,
    title: 'ที่ดิน',
    endpoint: 'create_land',
    dbName: 'KeppDataCreateLand',
    storeName: 'CreateLand',
    storageKey: 'LandData',
    floorPayloadKey: null,
    bedroomPayloadKey: null,
    bathroomPayloadKey: null,
    hasArea: false,
    hasUseArea: false,
    hasRooms: false,
    hasLandArea: true,
    hasFirstHand: false,
    hasFurniture: false,
    hasSellOut: false,
  },

  other: {
    ...base,
    title: 'โกดัง / โรงงาน',
    endpoint: 'other',
    dbName: 'KeppDataCreateOther',
    storeName: 'CreateOther',
    storageKey: 'OtherData',
    // Backend extracts condo field names for 'other' asset type
    floorPayloadKey: 'floor_condo',
    bedroomPayloadKey: 'bedrooms_condo',
    bathroomPayloadKey: 'bathrooms_condo',
    hasArea: false,
    hasDeeds: false,
    hasFirstHand: false,
    hasFurniture: false,
    hasSellOut: false,
    defaultUseAreaUnit: 'ตารางเมตร',
  },
};
